import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  LocalShippingOutlined,
  PeopleOutlined,
} from '@mui/icons-material';
import { Add } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { deleteImpactCo2Answer } from '@/domain/impact-co2/actions/delete-impact-co2-answer.action';
import { submitImpactCo2Answer } from '@/domain/impact-co2/actions/submit-impact-co2-answer.action';
import {
  CargoValues,
  Co2Section,
  ImpactCo2Answer,
  TransportationModeValues,
  ValuesCargo,
  ValuesMobility,
} from '@/domain/impact-co2/entities/question.entity';
import { selectImpactCo2Answers } from '@/domain/impact-co2/impact-co2.selectors';
import { TourTravel } from '@/domain/project-tour-ontour/entities/project-tour-ontour-travel.entity';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { language, translate } from '@/ui/i18n';
import { QuizFormSeparator } from '@/ui/pages/app/components/quiz-panel/co2/components/QuizFormSeparator';
import { ScoreCo2Label } from '@/ui/pages/app/components/quiz-panel/co2/components/ScoreCo2Label';
import { ImpactQuestionContext } from '@/ui/pages/app/components/quiz-panel/ImpactQuestionContext';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { isNumber } from '@/ui/utils/form-validations';

// ── Main export ──────────────────────────────────────────────────────────────

export function Co2FormTourTravel({ tourTravel }: { tourTravel: TourTravel }) {
  const dispatch = useAppDispatch();
  const projectId = tourTravel.id;
  const travelDistance = tourTravel.travelDistance;

  const onSubmit = (answer: ImpactCo2Answer) => {
    dispatch(
      submitImpactCo2Answer({ answer, projectId, submitMode: 'create' }),
    );
  };
  const onEdit = (answer: ImpactCo2Answer) => {
    dispatch(submitImpactCo2Answer({ answer, projectId, submitMode: 'edit' }));
  };
  const onDelete = (answer: ImpactCo2Answer) => {
    dispatch(deleteImpactCo2Answer({ answer, projectId }));
  };

  return (
    <ImpactQuestionContext.Provider
      value={{ panelOpen: true, hideCompleted: false }}
    >
      <Stack sx={{ flexDirection: 'row', gap: 2 }}>
        <Stack sx={{ flex: 1 }}>
          <QuizFormSeparator
            icon={<PeopleOutlined />}
            label={translate(`Co2.Sections.${Co2Section.MOBILITY}`)}
          />
          <MobilitySection
            travelDistance={travelDistance}
            onSubmit={onSubmit}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </Stack>
        <Stack sx={{ flex: 1 }}>
          <QuizFormSeparator
            icon={<LocalShippingOutlined />}
            label={translate(`Co2.Sections.${Co2Section.CARGO}`)}
          />
          <CargoSection
            travelDistance={travelDistance}
            onSubmit={onSubmit}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </Stack>
      </Stack>
    </ImpactQuestionContext.Provider>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const cardSx = {
  border: `1px solid ${ColorsRaw.anthracite.Anthracite_3}`,
  borderRadius: '8px',
  p: 1.5,
  mt: 1,
};

// ── Mobility ─────────────────────────────────────────────────────────────────

function MobilitySection({
  travelDistance,
  onSubmit,
  onEdit,
  onDelete,
}: {
  travelDistance: number;
  onSubmit: (a: ImpactCo2Answer) => void;
  onEdit: (a: ImpactCo2Answer) => void;
  onDelete: (a: ImpactCo2Answer) => void;
}) {
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const answersSelector = selectImpactCo2Answers<ValuesMobility>({
    sector: 'TEAM',
    category: 'MOBILITY',
    isExternal: false,
  });
  const answers = useAppSelector(answersSelector);

  const handleSubmit = (values: ValuesMobility) => {
    onSubmit({
      id: -1,
      userId: '',
      sector: 'TEAM',
      category: 'MOBILITY',
      values,
      isExternal: false,
      source: '',
    });
    setShowNew(false);
  };

  const handleEdit = (values: ValuesMobility, id: number) => {
    onEdit({
      id,
      userId: '',
      sector: 'TEAM',
      category: 'MOBILITY',
      values,
      isExternal: false,
      source: '',
    });
    setEditingId(null);
  };

  return (
    <Stack>
      {answers
        .slice()
        .sort((a, b) => a.id - b.id)
        .map((answer) =>
          editingId === answer.id ? (
            <Box key={answer.id} sx={cardSx}>
              <MobilityAnswerForm
                defaultValues={answer.values}
                travelDistance={travelDistance}
                onSubmit={(v) => handleEdit(v, answer.id)}
                onCancel={() => setEditingId(null)}
                onDelete={() => {
                  onDelete(answer);
                  setEditingId(null);
                }}
              />
            </Box>
          ) : (
            <MobilityAnswerCard
              key={answer.id}
              answer={answer}
              onEdit={() => setEditingId(answer.id)}
              onDelete={() => onDelete(answer)}
            />
          ),
        )}

      {showNew ? (
        <Box sx={cardSx}>
          <MobilityAnswerForm
            travelDistance={travelDistance}
            onSubmit={handleSubmit}
            onCancel={() => setShowNew(false)}
            showCancel={answers.length > 0}
          />
        </Box>
      ) : (
        <Button
          startIcon={<Add />}
          size="small"
          variant="outlined"
          sx={{ my: 2, alignSelf: 'center', textTransform: 'none' }}
          onClick={() => setShowNew(true)}
        >
          {translate('ProjectsTour.TourTravelForm.add-mobility')}
        </Button>
      )}
    </Stack>
  );
}

function MobilityAnswerCard({
  answer,
  onEdit,
  onDelete,
}: {
  answer: ImpactCo2Answer<ValuesMobility>;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { values } = answer;
  return (
    <Stack
      sx={{ ...cardSx, flexDirection: 'row', alignItems: 'center', gap: 1 }}
    >
      <Stack sx={{ flex: 1 }}>
        {values.label && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {values.label}
          </Typography>
        )}
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {translate(
            `ImpactQuestion.MOBILITY.transportationModeValues.${values.transportationMode}`,
          )}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {values.passengerCount}{' '}
          {translate(
            values.passengerCount > 1
              ? 'ImpactQuestion.MOBILITY.people'
              : 'ImpactQuestion.MOBILITY.person',
          )}
          {' · '}
          {values.travelDistance.toLocaleString(language)} km
        </Typography>
      </Stack>
      {values.score !== undefined && <ScoreCo2Label score={values.score} />}
      <IconButton size="small" onClick={onEdit}>
        <EditOutlined sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton size="small" onClick={onDelete}>
        <DeleteOutlined sx={{ fontSize: 16 }} />
      </IconButton>
    </Stack>
  );
}

type MobilityFields = {
  label: string;
  transportationMode: ValuesMobility['transportationMode'];
  passengerCount: number;
};

function MobilityAnswerForm({
  defaultValues,
  travelDistance,
  onSubmit,
  onCancel,
  onDelete,
  showCancel = true,
}: {
  defaultValues?: ValuesMobility;
  travelDistance: number;
  onSubmit: (v: ValuesMobility) => void;
  onCancel: () => void;
  onDelete?: () => void;
  showCancel?: boolean;
}) {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<MobilityFields>({
    defaultValues: {
      label: defaultValues?.label ?? '',
      transportationMode: defaultValues?.transportationMode ?? 'BICYCLE',
      passengerCount: defaultValues?.passengerCount ?? 0,
    },
  });

  const onFormSubmit = (data: MobilityFields) => {
    onSubmit({
      ...data,
      travelDistance,
      passengerCount: parseInt(data.passengerCount.toString()),
      score: defaultValues?.score ?? 0,
      tags: defaultValues?.tags ?? [],
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <Stack sx={{ gap: 1 }}>
        <FSTextField
          type="text"
          defaultValue={defaultValues?.label ?? ''}
          register={{ ...register('label') }}
          placeholder={translate(
            'ProjectsTour.TourTravelForm.travel-answer-name',
          )}
          placeholderColor="#A29BAE"
          variant="standard"
          size="small"
        />
        <Stack sx={{ flexDirection: 'row', gap: 1, alignItems: 'flex-end' }}>
          <Box sx={{ flex: 1 }}>
            <FSTextField
              select
              defaultValue={defaultValues?.transportationMode ?? 'BICYCLE'}
              label={translate(
                'ImpactQuestion.MOBILITY.transportationModeTitle',
              )}
              register={{ ...register('transportationMode') }}
              error={!!errors.transportationMode}
              size="small"
            >
              {TransportationModeValues.map((value) => (
                <MenuItem key={value} value={value}>
                  {translate(
                    `ImpactQuestion.MOBILITY.transportationModeValues.${value}`,
                  )}
                </MenuItem>
              ))}
            </FSTextField>
          </Box>
          <Box sx={{ width: '5rem' }}>
            <FSTextField
              type="number"
              defaultValue={defaultValues?.passengerCount ?? 0}
              register={{
                ...register('passengerCount', {
                  required: translate('Form.fill-the-field'),
                  validate: isNumber,
                }),
              }}
              error={!!errors.passengerCount}
              helperText={errors.passengerCount?.message}
              size="small"
            />
          </Box>
          <Typography
            sx={{ fontSize: '0.875rem', mb: 1, whiteSpace: 'nowrap' }}
          >
            {translate('ImpactQuestion.MOBILITY.people')}
          </Typography>
        </Stack>
        <Stack
          sx={{ flexDirection: 'row', gap: 1, mt: 0.5, alignItems: 'center' }}
        >
          <FSPrimaryButton type="submit">
            {translate(defaultValues ? 'Form.save' : 'Form.add')}
          </FSPrimaryButton>
          {onDelete && (
            <IconButton size="small" onClick={onDelete}>
              <DeleteOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          {showCancel && (
            <IconButton size="small" onClick={onCancel}>
              <CloseOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </form>
  );
}

// ── Cargo ─────────────────────────────────────────────────────────────────────

function CargoSection({
  travelDistance,
  onSubmit,
  onEdit,
  onDelete,
}: {
  travelDistance: number;
  onSubmit: (a: ImpactCo2Answer) => void;
  onEdit: (a: ImpactCo2Answer) => void;
  onDelete: (a: ImpactCo2Answer) => void;
}) {
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const answersSelector = selectImpactCo2Answers<ValuesCargo>({
    sector: 'TEAM',
    category: 'CARGO',
    isExternal: false,
  });
  const answers = useAppSelector(answersSelector);

  const handleSubmit = (values: ValuesCargo) => {
    onSubmit({
      id: -1,
      userId: '',
      sector: 'TEAM',
      category: 'CARGO',
      values,
      isExternal: false,
      source: '',
    });
    setShowNew(false);
  };

  const handleEdit = (values: ValuesCargo, id: number) => {
    onEdit({
      id,
      userId: '',
      sector: 'TEAM',
      category: 'CARGO',
      values,
      isExternal: false,
      source: '',
    });
    setEditingId(null);
  };

  return (
    <Stack>
      {answers
        .slice()
        .sort((a, b) => a.id - b.id)
        .map((answer) =>
          editingId === answer.id ? (
            <Box key={answer.id} sx={cardSx}>
              <CargoAnswerForm
                defaultValues={answer.values}
                travelDistance={travelDistance}
                onSubmit={(v) => handleEdit(v, answer.id)}
                onCancel={() => setEditingId(null)}
                onDelete={() => {
                  onDelete(answer);
                  setEditingId(null);
                }}
              />
            </Box>
          ) : (
            <CargoAnswerCard
              key={answer.id}
              answer={answer}
              onEdit={() => setEditingId(answer.id)}
              onDelete={() => onDelete(answer)}
            />
          ),
        )}

      {showNew ? (
        <Box sx={cardSx}>
          <CargoAnswerForm
            travelDistance={travelDistance}
            onSubmit={handleSubmit}
            onCancel={() => setShowNew(false)}
            showCancel={answers.length > 0}
          />
        </Box>
      ) : (
        <Button
          startIcon={<Add />}
          size="small"
          variant="outlined"
          sx={{ my: 2, alignSelf: 'center', textTransform: 'none' }}
          onClick={() => setShowNew(true)}
        >
          {translate('ProjectsTour.TourTravelForm.add-cargo')}
        </Button>
      )}
    </Stack>
  );
}

function CargoAnswerCard({
  answer,
  onEdit,
  onDelete,
}: {
  answer: ImpactCo2Answer<ValuesCargo>;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { values } = answer;
  return (
    <Stack
      sx={{ ...cardSx, flexDirection: 'row', alignItems: 'center', gap: 1 }}
    >
      <Stack sx={{ flex: 1 }}>
        {values.label && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {values.label}
          </Typography>
        )}
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {translate(
            `ImpactQuestion.CARGO.transportationModeValues.${values.transportationMode}`,
          )}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {values.weight.toLocaleString(language)}{' '}
          {translate('ImpactQuestion.CARGO.weight-unit', {
            count: values.weight,
          })}
          {' · '}
          {values.travelDistance.toLocaleString(language)} km
        </Typography>
      </Stack>
      {values.score !== undefined && <ScoreCo2Label score={values.score} />}
      <IconButton size="small" onClick={onEdit}>
        <EditOutlined sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton size="small" onClick={onDelete}>
        <DeleteOutlined sx={{ fontSize: 16 }} />
      </IconButton>
    </Stack>
  );
}

type CargoFields = {
  label: string;
  transportationMode: ValuesCargo['transportationMode'];
  weight: number;
};

function CargoAnswerForm({
  defaultValues,
  travelDistance,
  onSubmit,
  onCancel,
  onDelete,
  showCancel = true,
}: {
  defaultValues?: ValuesCargo;
  travelDistance: number;
  onSubmit: (v: ValuesCargo) => void;
  onCancel: () => void;
  onDelete?: () => void;
  showCancel?: boolean;
}) {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<CargoFields>({
    defaultValues: {
      label: defaultValues?.label ?? '',
      transportationMode: defaultValues?.transportationMode ?? 'VAN',
      weight: defaultValues?.weight ?? 0,
    },
  });

  const onFormSubmit = (data: CargoFields) => {
    onSubmit({
      ...data,
      travelDistance,
      weight: parseFloat(data.weight.toString()),
      score: defaultValues?.score ?? 0,
      tags: defaultValues?.tags ?? [],
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <Stack sx={{ gap: 1 }}>
        <FSTextField
          type="text"
          defaultValue={defaultValues?.label ?? ''}
          register={{ ...register('label') }}
          placeholder={translate(
            'ProjectsTour.TourTravelForm.travel-answer-name',
          )}
          placeholderColor="#A29BAE"
          variant="standard"
          size="small"
        />
        <Stack sx={{ flexDirection: 'row', gap: 1, alignItems: 'flex-end' }}>
          <Box sx={{ flex: 1 }}>
            <FSTextField
              select
              defaultValue={defaultValues?.transportationMode ?? 'VAN'}
              label={translate(
                `ImpactQuestion.CARGO.transportationModeTitleBySector.TEAM`,
              )}
              register={{ ...register('transportationMode') }}
              error={!!errors.transportationMode}
              size="small"
            >
              {CargoValues.map((value) => (
                <MenuItem key={value} value={value}>
                  {translate(
                    `ImpactQuestion.CARGO.transportationModeValues.${value}`,
                  )}
                </MenuItem>
              ))}
            </FSTextField>
          </Box>
          <Box sx={{ width: '5rem' }}>
            <FSTextField
              type="number"
              defaultValue={defaultValues?.weight ?? 0}
              register={{
                ...register('weight', {
                  required: translate('Form.fill-the-field'),
                  validate: isNumber,
                }),
              }}
              error={!!errors.weight}
              helperText={errors.weight?.message}
              step={0.01}
              size="small"
            />
          </Box>
          <Typography
            sx={{ fontSize: '0.875rem', mb: 1, whiteSpace: 'nowrap' }}
          >
            T
          </Typography>
        </Stack>
        <Stack
          sx={{ flexDirection: 'row', gap: 1, mt: 0.5, alignItems: 'center' }}
        >
          <FSPrimaryButton type="submit">
            {translate(defaultValues ? 'Form.save' : 'Form.add')}
          </FSPrimaryButton>
          {onDelete && (
            <IconButton size="small" onClick={onDelete}>
              <DeleteOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          {showCancel && (
            <IconButton size="small" onClick={onCancel}>
              <CloseOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </form>
  );
}
